import { Color } from '../../../../../../widgets/colorsPalette';
import { Icon } from '../../../../../../widgets/icon';
import React, { FC } from 'react';
import { T } from 'src/shared/helpers';
import ArrowDownIcon from 'src/shared/material_icons/navigation/arrow_downward.svg';
import ArrowUpIcon from 'src/shared/material_icons/navigation/arrow_upward.svg';
import HiddenIcon from 'src/shared/material_icons/action/visibility_off.svg';
import VisibleIcon from 'src/shared/material_icons/action/visibility.svg';
import PlayIcon from 'src/shared/material_icons/av/play_arrow.svg';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { ComposerOptionActionNode, ComposerOptionActionsType } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { getCapitalizedFormattedName } from '../helpers/composerHelpers';

interface ComposerAttributeTreeElementContextMenuData {
    onContextMenuItemClick: (event: React.MouseEvent<HTMLDivElement>, action: ComposerOptionActionNode) => void;
    contextMenu: React.ReactNode;
}

interface ComposerActionTreeElementProps {
    action: ComposerOptionActionNode;
    findMesh: (meshId: string) => BABYLON.AbstractMesh | undefined;
    findMaterial: (materialId: string) => BABYLON.Material | undefined;
    findCamera: (cameraId: string) => BABYLON.Camera | undefined;
    onSelect: (event: React.MouseEvent<HTMLDivElement>, action: ComposerOptionActionNode) => void;
    onPlay?: (action: ComposerOptionActionNode) => void;
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>, action: ComposerOptionActionNode) => void;
    onMoveUp?: (action: ComposerOptionActionNode) => void;
    onMoveDown?: (action: ComposerOptionActionNode) => void;
    onDragStart?: (e: React.DragEvent<HTMLDivElement>, action: ComposerOptionActionNode) => void;
    onDragEnd?: (e: React.DragEvent<HTMLDivElement>, action: ComposerOptionActionNode) => void;
    onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
    onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
    contextMenu?: ComposerAttributeTreeElementContextMenuData;
    selected: boolean;
}

const ComposerAttributeTreeElementContainer = styled.div<{
    selected?: boolean;
}>`
    display: flex;
    align-items: center;
    padding: 4px 8px;
    padding-left: 12px;
    margin-left: 0px;
    background-color: transparent;
    border: ${(props) => (props.selected ? '2px' : '1px')} solid ${(props) => (props.selected ? theme.app.secondary : theme.app.borderColor)};
    color: ${theme.app.textColor};
    border-radius: 4px;
    cursor: pointer;
    height: 48px;
    min-height: 48px;
    width: 100%;
    user-select: none;
    transition:
        background-color 0.2s ease,
        border-color 0.2s ease;

    &:hover {
        background-color: ${theme.app.backgroundColor};
        border-color: ${theme.app.secondary};
        opacity: ${(props) => (props.selected ? 1 : 0.8)};
    }
`;

const ComposerAttributeTreeElementHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: start;
    width: 100%;
    height: 100%;
    position: relative;

    gap: 8px;
`;

const ComposerAttributeTreeInnerElementContainer = styled.div`
    display: flex;
    min-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ComposerAttributeTreeTextElementNameContainer = styled.div<{ $title?: boolean }>`
    width: 150px;
    margin-right: 20px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: ${(props) => (props.$title ? 'bold' : 'normal')};
`;

const ComposerAttributeTreeElementRightContainer = styled.div`
    position: absolute;
    right: 8px;
    display: flex;
    align-items: center;
    flex-grow: 1;
    flex-shrink: 0; /* Prevents container from shrinking */
    min-width: 30px;
`;

const ComposerAttributeTreeElementImageContainer = styled.div`
    width: 24px;
    height: 24px;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;

    & > img,
    svg {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;
const IconContainer = styled.div`
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`;

const UNDEFINED_PLACEHOLDER = '---';

export const ComposerActionTreeElement: FC<ComposerActionTreeElementProps> = ({
    action,
    onSelect,
    onDoubleClick,
    onPlay,
    findMesh,
    findMaterial,
    findCamera,
    contextMenu,
    selected,
    onMoveUp,
    onMoveDown,
    onDragStart,
    onDragEnd,
    onDrop,
    onDragOver
}) => {
    const [showContextMenu, setShowContextMenu] = React.useState(false);

    const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
        if (contextMenu) {
            event.preventDefault();
            event.stopPropagation();
            setShowContextMenu(true);
            contextMenu.onContextMenuItemClick(event, action);
        }
    };

    const aggregator = (func: (s: string) => BABYLON.AbstractMesh | BABYLON.Material | BABYLON.Camera | undefined, selector: string): string => {
        return selector
            .split(',')
            .map((id) => func(id.trim())?.name ?? id.trim())
            .join(', ');
    };

    const [selector, value] = React.useMemo(() => {
        switch (action.type) {
            case ComposerOptionActionsType.SHOW_OBJECTS:
                return [
                    aggregator(findMesh, action.selector) || UNDEFINED_PLACEHOLDER,
                    <Icon>
                        <VisibleIcon />
                    </Icon>
                ];
            case ComposerOptionActionsType.HIDE_OBJECTS:
                return [
                    aggregator(findMesh, action.selector) || UNDEFINED_PLACEHOLDER,
                    <Icon>
                        <HiddenIcon />
                    </Icon>
                ];
            case ComposerOptionActionsType.SET_OBJECT_MATERIAL:
                return [aggregator(findMesh, action.selector) || UNDEFINED_PLACEHOLDER, aggregator(findMaterial, action.value) || UNDEFINED_PLACEHOLDER];
            case ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL:
                return [aggregator(findMesh, action.selector) || UNDEFINED_PLACEHOLDER, aggregator(findMaterial, action.value) || UNDEFINED_PLACEHOLDER];
            case ComposerOptionActionsType.SET_MATERIAL_COLOR:
                return [aggregator(findMaterial, action.selector) || UNDEFINED_PLACEHOLDER, <Color color={action.value} />];
            case ComposerOptionActionsType.SET_MATERIAL_PARAMETER:
                return [aggregator(findMaterial, action.selector) || UNDEFINED_PLACEHOLDER, `${action.property}: ${action.value}`];
            case ComposerOptionActionsType.MOVE_CAMERA:
                return [aggregator(findCamera, action.selector) || UNDEFINED_PLACEHOLDER, aggregator(findCamera, action.value) || UNDEFINED_PLACEHOLDER];
            case ComposerOptionActionsType.SET_DESIGN_MATERIAL:
                return [aggregator(findMesh, action.selector) || UNDEFINED_PLACEHOLDER, aggregator(findMaterial, action.value) || UNDEFINED_PLACEHOLDER];
            case ComposerOptionActionsType.SET_CAMERA_PIVOT:
                return [UNDEFINED_PLACEHOLDER, aggregator(findCamera, action.value) || UNDEFINED_PLACEHOLDER];
            case ComposerOptionActionsType.RESET_CAMERA_PIVOT:
                return [UNDEFINED_PLACEHOLDER, UNDEFINED_PLACEHOLDER];
            case ComposerOptionActionsType.SET_PRINTING_METHOD:
                return [UNDEFINED_PLACEHOLDER, action.value || UNDEFINED_PLACEHOLDER];
            default:
                return [UNDEFINED_PLACEHOLDER, UNDEFINED_PLACEHOLDER];
        }
    }, [action]);

    const getValue = React.useMemo(() => {}, [action]);

    const name = T._(getCapitalizedFormattedName(ComposerOptionActionsType[action.type]), 'Admin');

    return (
        <>
            <ComposerAttributeTreeElementContainer
                selected={selected}
                onClick={(e) => onSelect(e, action)}
                onDoubleClick={onDoubleClick ? (e) => onDoubleClick(e, action) : undefined}
                onContextMenu={handleContextMenu}
                draggable={!!onDragStart}
                onDragStart={(e) => onDragStart && onDragStart(e, action)}
                onDragEnd={(e) => onDragEnd && onDragEnd(e, action)}
                onDragOver={onDragOver}
                onDrop={onDrop}
            >
                <ComposerAttributeTreeElementHeader>
                    <Icon
                        tooltip={T._('Execute the action', 'Admin')}
                        onClick={(e) => {
                            e.stopPropagation();
                            onPlay?.(action);
                        }}
                    >
                        <PlayIcon />
                    </Icon>
                    <ComposerAttributeTreeInnerElementContainer>
                        <ComposerAttributeTreeTextElementNameContainer $title>{name}</ComposerAttributeTreeTextElementNameContainer>
                    </ComposerAttributeTreeInnerElementContainer>
                    <ComposerAttributeTreeTextElementNameContainer>{selector}</ComposerAttributeTreeTextElementNameContainer>

                    <ComposerAttributeTreeTextElementNameContainer>{value}</ComposerAttributeTreeTextElementNameContainer>
                    <ComposerAttributeTreeElementRightContainer>
                        <IconContainer
                            onClick={(e) => {
                                e.stopPropagation();
                                onMoveUp && onMoveUp(action);
                            }}
                        >
                            <ArrowUpIcon fill={theme.app.textColor} />
                        </IconContainer>
                        <IconContainer
                            onClick={(e) => {
                                e.stopPropagation();
                                onMoveDown && onMoveDown(action);
                            }}
                        >
                            <ArrowDownIcon fill={theme.app.textColor} />
                        </IconContainer>
                    </ComposerAttributeTreeElementRightContainer>
                </ComposerAttributeTreeElementHeader>
                {showContextMenu && contextMenu && contextMenu.contextMenu}
            </ComposerAttributeTreeElementContainer>
        </>
    );
};
